import { observer } from 'mobx-react-lite';
import { motion } from 'motion/react';
import { useCallback, useEffect, useRef } from 'react';

import { Icons } from '@/assets/icons/icons';
import { IconButton } from '@/components/ui';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/Accordion';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { littlebirdSdk } from '@/lib/sdk-init';

import { AgentConfigViewer } from './agent-config';
import { ChainOfThoughtsViewer } from './chain-of-thoughts';
import { ChatDebugHeader } from './chat-debug.header';
import { chatDebugStore } from './chat-debug.store';
import { LlmCostsViewer } from './llm-costs/llm-costs.viewer';
import { TraceViewerLayout } from './trace-viewer';

const LLM_COSTS_ACCORDION_KEY = 'accordion:llm-costs';

export const ChatDebugLayout = observer(() => {
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(chatDebugStore.sidebarWidth);

  const onDragStart = useCallback((e: React.MouseEvent) => {
    isDraggingRef.current = true;
    startXRef.current = e.clientX;
    startWidthRef.current = chatDebugStore.sidebarWidth;
    e.preventDefault();
  }, []);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) return;
      const delta = startXRef.current - e.clientX;
      chatDebugStore.setSidebarWidth(startWidthRef.current + delta);
    };
    const onMouseUp = () => {
      isDraggingRef.current = false;
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const session = chatDebugStore.active;
  const sidebarWidth = chatDebugStore.sidebarWidth;
  const threadFinished = session
    ? littlebirdSdk.stores.threads.isThreadFinished(session.chatId)
    : undefined;

  useEffect(() => {
    if (!session) return;
    if (threadFinished === true) {
      if (session.payloadSkeletons || session.chatCostSummary) {
        void session.fetchDebugData();
      } else {
        session.ensureDebugData();
      }
      void session.fetchTracesForChat();
      return;
    }
    return session.startLiveTraceRefresh();
  }, [session, threadFinished]);

  if (!session) return null;

  const messages = session.messages;
  const isLoadingTraces = session.loadingTracesForChat;
  const llmCostsAccordionValue = session.isSectionExpanded(
    LLM_COSTS_ACCORDION_KEY,
    true
  )
    ? ['llm-costs']
    : [];

  return (
    <motion.div
      initial={{ width: 0, opacity: 0 }}
      animate={{ width: sidebarWidth, opacity: 1 }}
      exit={{ width: 0, opacity: 0 }}
      transition={
        isDraggingRef.current
          ? { duration: 0 }
          : { duration: 0.2, ease: 'easeInOut' }
      }
      className="flex flex-col h-full border-l border-border bg-background shrink-0 overflow-hidden relative max-w-[60vw]"
    >
      <div
        className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-500/30 transition-colors z-10"
        onMouseDown={onDragStart}
      />
      <XStack className="items-center justify-between px-3 py-2 border-b border-border shrink-0">
        <span className="text-sm font-semibold text-foreground">
          Chat Debug
        </span>
        <IconButton
          size="sm"
          icon={Icons.cancel}
          onClick={() => session.setIsOpen(false)}
          tooltip="Close debug panel"
        />
      </XStack>

      <YStack className="flex-1 overflow-y-auto">
        {session.payloadsError && (
          <p
            role="status"
            className="mx-3 mt-3 rounded-md border border-border bg-background-secondary p-3 text-xs text-foreground-muted"
          >
            {session.payloadsError}
          </p>
        )}
        <Accordion
          type="multiple"
          value={llmCostsAccordionValue}
          onValueChange={(openValues) =>
            session.setSectionExpanded(
              LLM_COSTS_ACCORDION_KEY,
              openValues.includes('llm-costs')
            )
          }
          className="flex flex-col border-b border-border"
        >
          <AccordionItem value="llm-costs" className="border-none">
            <XStack className="items-center pr-2">
              <AccordionTrigger className="flex-1 px-3 py-2 hover:bg-background-secondary">
                <Icons.chevronDown className="mr-2 size-4 shrink-0 transition-transform duration-200" />
                <XStack className="items-center gap-2">
                  <span className="font-medium">LLM Costs</span>
                  <Icons.wallet className="h-4 w-4 !transform-none" />
                </XStack>
              </AccordionTrigger>
              <IconButton
                size="sm"
                icon={Icons.refresh}
                onClick={(e) => {
                  e.stopPropagation();
                  session.fetchDebugData();
                }}
                disabled={session.loadingCosts}
                tooltip="Reload payload & cost data"
                className={session.loadingCosts ? '[&_svg]:animate-spin' : ''}
              />
            </XStack>
            <AccordionContent className="px-3">
              <LlmCostsViewer />
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <YStack className="gap-2 pt-3 pb-0">
          <span className="px-3 text-[10px] font-semibold uppercase tracking-widest text-foreground-subtle">
            Per Message Analytics
          </span>
          {isLoadingTraces ? (
            <YStack className="items-center justify-center py-6">
              <Icons.refresh className="size-5 animate-spin text-foreground-subtle" />
            </YStack>
          ) : !messages.length ? (
            <YStack className="items-center justify-center gap-2 py-6">
              <span className="text-sm text-foreground-subtle">
                No messages available
              </span>
              <button
                onClick={() => session.fetchTracesForChat()}
                className="text-sm text-foreground-subtle underline hover:text-foreground"
              >
                Click to refresh
              </button>
            </YStack>
          ) : (
            <>
              <ChatDebugHeader />
              <YStack className="gap-3 p-3">
                <Accordion
                  type="multiple"
                  defaultValue={['agent-config']}
                  className="flex flex-col gap-3"
                >
                  <AccordionItem
                    value="agent-config"
                    className="rounded-lg border border-border"
                  >
                    <AccordionTrigger className="px-3 py-2 hover:bg-background-secondary">
                      <Icons.chevronDown className="mr-2 size-4 shrink-0 transition-transform duration-200" />
                      <XStack className="items-center gap-2">
                        <span className="font-medium">Agent Config</span>
                        <Icons.settings className="h-4 w-4 !transform-none" />
                      </XStack>
                    </AccordionTrigger>
                    <AccordionContent className="px-3">
                      {session.payloadsError ? (
                        <p className="py-2 text-xs text-foreground-subtle">
                          Payload details are unavailable.
                        </p>
                      ) : (
                        <AgentConfigViewer
                          payloads={session.activeRequestPayloads}
                        />
                      )}
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem
                    value="call-trace"
                    className="rounded-lg border border-border"
                  >
                    <AccordionTrigger className="px-3 py-2 hover:bg-background-secondary">
                      <Icons.chevronDown className="mr-2 size-4 shrink-0 transition-transform duration-200" />
                      <XStack className="items-center gap-2 !transition-none">
                        <span className="font-medium">Call Trace</span>
                        <Icons.folderTree className="h-4 w-4 !transform-none" />
                      </XStack>
                    </AccordionTrigger>
                    <AccordionContent className="px-3">
                      <TraceViewerLayout
                        traceTree={session.traceTree}
                        isLoading={session.loadingTraceForMessage}
                        onRefresh={() => session.fetchTracesForChat()}
                      />
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem
                    value="chain-of-thoughts"
                    className="rounded-lg border border-border"
                  >
                    <AccordionTrigger className="px-3 py-2 hover:bg-background-secondary">
                      <Icons.chevronDown className="mr-2 size-4 shrink-0 transition-transform duration-200" />
                      <XStack className="items-center gap-2">
                        <span className="font-medium">Chain of Thoughts</span>
                        <Icons.memory className="h-4 w-4 !transform-none" />
                      </XStack>
                    </AccordionTrigger>
                    <AccordionContent className="px-3">
                      <ChainOfThoughtsViewer
                        responses={session.activeMessageResponses}
                      />
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </YStack>
            </>
          )}
        </YStack>
      </YStack>
    </motion.div>
  );
});
